import React, {useEffect, useMemo, useState} from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrModal, {ArrModalProps} from '../../common/arr-modal/ArrModal';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {IconButton, Typography} from '@mui/material';
import {t} from 'i18next';
import ActionLike from './action-like/ActionLike';
import './ActionLikeDocuments.scss';
import {
  useGetBulkActionAllDocumentCount,
  useGetBulkTagSelectedDocCount,
  useGetLikeDocumentCountNonBG,
} from '../../../hooks/assignments/useAssignments';
import {ILikeDocumentCountRequest} from '../../../api/types/assignment.type';
import {SourceTypeCode} from '../../../shared/constants';
// Types Definitions
// ----------------------------------
export interface IActionListItem {
  id: number | string;
  label: string;
  selected?: boolean;
  isLoading?: boolean;
  count: number;
}
const enum AnalyticsStatus {
  AnalyticsInProgress = 1,
  AnalyticsSkipped = 2,
  AnalyticsNoActionRequire = 3,
  AnalyticsFailed = 4,
}

interface ActionLikeDocumentsProps extends ArrModalProps {
  actionList: IActionListItem[];
  selectedIds?: string[];
  onActionChange?: (
    index: number,
    status: boolean,
    updatedActionItems: IActionListItem[]
  ) => void;
  onCancel: () => void;
  onContinue: (docCount?: number) => void;
  continueButtonLabel?: string;
  errorMessage?: string;
  masterDocListId?: number;
  actionType?: 'ASSIGNMENT' | 'TAG' | 'FOLDER' | 'QUICKASSIGNMENT' | 'RELEASE';
  analyticsIndicatorStatus: number;

  isNonBg?: boolean;
  nonBgAssignmentId?: number;
  nonBgReviewerId?: number;
  nonBgDocCount?: number;
  sourceTypeCode?: SourceTypeCode;
}
// ------------------

const getT = (id: string) => {
  return t(`actionMenu.actionLikeDocuments.${id}`);
};

// ----------------------------------
// ActionLikeDocuments Component
// ----------------------------------
const ActionLikeDocuments: React.FC<ActionLikeDocumentsProps> = ({
  title = t('docExplorer.actionMenu.actionLikeDocuments.title'),
  showModal = false,
  continueButtonLabel = '',
  onClose,
  onContinue,
  onCancel,
  actionList: initialActionList,
  onActionChange,
  masterDocListId = 0,
  actionType,
  analyticsIndicatorStatus,
  isNonBg = false,
  nonBgAssignmentId = 0,
  nonBgReviewerId = 0,
  nonBgDocCount = 0,
  sourceTypeCode = 'ASSIGNMENTREVIEWER',
  dataTestId,
  ...props
}) => {
  const ActionLikeDescription = useMemo(
    () => (
      <>
        <Typography data-testid="action-like-documents-description-p1">
          {getT('descriptionP1')}
        </Typography>
        <Typography data-testid="action-like-documents-typography">
          &nbsp;
        </Typography>
        <Typography data-testid="action-like-documents-description-p2">
          {getT('descriptionP2')}
        </Typography>
      </>
    ),
    []
  );

  const [actionToggles, setActionToggles] = useState<IActionListItem[]>(
    initialActionList.map((l) => ({
      ...l,
      selected: false,
      isLoading: isNonBg ? true : false,
    }))
  );

  // added for nonbgpayload
  const nonBgPayload = useMemo<ILikeDocumentCountRequest | null>(() => {
    if (
      !isNonBg ||
      !showModal ||
      ((!nonBgAssignmentId || nonBgAssignmentId <= 0) &&
        (!nonBgReviewerId || nonBgReviewerId <= 0))
    ) {
      return null;
    }

    return {
      sourceType: sourceTypeCode,
      sourceDetails: [
        {
          id: nonBgAssignmentId,
          includePureHits: true,
          includeFamily: true,
          includeEmailThread: true,
          includeNearDupe: true,
          includeConcept: true,
          includeUniquePureHits: true,
          includeUniqueFamilyMembers: true,
        },
      ],
      contextContainer: {
        contextContainerId: nonBgAssignmentId,
        bulkActionContexts: [
          {
            id:
              sourceTypeCode === SourceTypeCode.ASSIGNMENTREVIEWER
                ? nonBgReviewerId
                : nonBgAssignmentId,
            includePureHits: true,
            includeFamily: true,
            includeEmailThread: true,
            includeNearDupe: true,
            includeConcept: true,
            includeUniquePureHits: true,
            includeUniqueFamilyMembers: true,
          },
        ],
      },
      numberToAssign: nonBgDocCount,
      sampleMethod: 'STANDARD',
    };
  }, [isNonBg, showModal, nonBgAssignmentId, nonBgReviewerId, nonBgDocCount]); // ✅ ADD nonBgDocCount dependency

  const {
    data: nonBgCountData,
    isLoading: isLoadingNonBgCount,
    isFetching: isFetchingNonBgCount,
  } = useGetLikeDocumentCountNonBG(nonBgPayload, isNonBg && showModal);

  //  BG Flow - Background count data (ONLY for BG mode)
  const {data: backgroundCountData} = useGetBulkTagSelectedDocCount(
    {masterDocListId},
    !isNonBg && masterDocListId > 0 && showModal // !isNonBg check
  );

  const shouldFetchAllDocCount =
    !isNonBg && // CRITICAL: Only run in BG mode
    masterDocListId > 0 &&
    showModal &&
    (actionType === 'ASSIGNMENT' ||
    actionType === 'QUICKASSIGNMENT' ||
    actionType === 'RELEASE'
      ? (backgroundCountData?.docCount ?? 0) > 0
      : true);

  const {data: allDocCount, isLoading: isLoadingAllDocCount} =
    useGetBulkActionAllDocumentCount({masterDocListId}, shouldFetchAllDocCount);

  const isLoadingBackgroundCount =
    !isNonBg && // Only for BG mode
    (actionType === 'ASSIGNMENT' || actionType === 'QUICKASSIGNMENT') &&
    masterDocListId > 0 &&
    showModal &&
    (backgroundCountData?.docCount ?? 0) === 0;

  useEffect(() => {
    if (showModal) {
      setActionToggles(initialActionList.map((l) => ({...l, selected: false})));
    }
  }, [showModal]);
  //  Non-BG flow effect
  useEffect(() => {
    if (!isNonBg || !showModal) return;

    if (nonBgCountData && !isLoadingNonBgCount && !isFetchingNonBgCount) {
      setActionToggles((prev) =>
        prev.map((item) => {
          switch (item.id) {
            case 'family_members':
              return {
                ...item,
                count: nonBgCountData.familyMembersCount ?? 0,
                isLoading: false,
              };
            case 'email_docs':
              return {
                ...item,
                count: nonBgCountData.emailThreadsCount ?? 0,
                isLoading: false,
              };
            case 'near_duplicates':
              return {
                ...item,
                count: nonBgCountData.nearDuplicatesCount ?? 0,
                isLoading: false,
              };
            default:
              return {...item, isLoading: false};
          }
        })
      );
    } else if (isLoadingNonBgCount || isFetchingNonBgCount) {
      setActionToggles((prev) =>
        prev.map((item) => ({
          ...item,
          isLoading: true,
        }))
      );
    }
  }, [
    isNonBg,
    showModal,
    nonBgCountData,
    isLoadingNonBgCount,
    isFetchingNonBgCount,
  ]);

  useEffect(() => {
    if (isNonBg) return; //   Skip if non-BG

    if (isLoadingAllDocCount) {
      setActionToggles((prevActionToggles) => {
        const updatedActionList = prevActionToggles.map((item) => ({
          ...item,
          isLoading: true,
        }));
        return updatedActionList;
      });
      return;
    }
    if (!allDocCount?.likePopupCount) return;

    const countMapping: Record<
      string,
      keyof typeof allDocCount.likePopupCount
    > = {
      family_members: 'f',
      email_docs: 'e',
      near_duplicates: 'd',
    };

    setActionToggles((prevActionToggles) => {
      const updatedActionList = prevActionToggles.map((item) => ({
        ...item,
        count: allDocCount.likePopupCount[countMapping[item.id]] ?? 0,
        isLoading: false,
      }));
      return updatedActionList;
    });
  }, [isNonBg, allDocCount, isLoadingAllDocCount]);

  useEffect(() => {
    if (!isNonBg || !showModal) return;

    if (isLoadingNonBgCount || isFetchingNonBgCount) {
      setActionToggles((prev) =>
        prev.map((item) => ({
          ...item,
          isLoading: true,
        }))
      );
      return;
    }

    if (nonBgCountData) {
      setActionToggles((prev) =>
        prev.map((item) => {
          let count = 0;

          switch (item.id) {
            case 'family_members':
              count = nonBgCountData.familyMembersCount ?? 0;
              break;
            case 'email_docs':
              count = nonBgCountData.emailThreadsCount ?? 0;
              break;
            case 'near_duplicates':
              count = nonBgCountData.nearDuplicatesCount ?? 0;
              break;
            default:
              count = item.count;
          }

          return {
            ...item,
            count,
            isLoading: false,
          };
        })
      );
    } else {
      setActionToggles((prev) =>
        prev.map((item) => ({
          ...item,
          isLoading: false,
        }))
      );
    }
  }, [
    isNonBg,
    showModal,
    nonBgCountData,
    isLoadingNonBgCount,
    isFetchingNonBgCount,
  ]);

  // ------------------

  const handleActionToggle = (index: number, status: boolean) => {
    const updatedActionItems = [...actionToggles];
    updatedActionItems[index].selected = status;
    setActionToggles(updatedActionItems);

    if (onActionChange) {
      onActionChange(index, status, updatedActionItems);
    }
  };

  const isLoading = useMemo(() => {
    if (isNonBg) {
      return isLoadingNonBgCount || isFetchingNonBgCount;
    }
    return (
      ((actionType === 'ASSIGNMENT' || actionType === 'QUICKASSIGNMENT') &&
        isLoadingBackgroundCount) ||
      isLoadingAllDocCount
    );
  }, [
    isNonBg,
    isLoadingNonBgCount,
    isFetchingNonBgCount,
    actionType,
    isLoadingAllDocCount,
    isLoadingBackgroundCount,
  ]);

  const totalSelectedCount = useMemo(() => {
    if (isNonBg) {
      if (!nonBgCountData) return nonBgDocCount;

      const selected = actionToggles.filter((x) => x.selected);

      if (selected.length === 0) {
        return nonBgDocCount;
      }

      let total = nonBgDocCount;
      selected.forEach((item) => {
        if (item.id === 'family_members')
          total += nonBgCountData.familyMembersCount ?? 0;
        if (item.id === 'email_docs')
          total += nonBgCountData.emailThreadsCount ?? 0;
        if (item.id === 'near_duplicates')
          total += nonBgCountData.nearDuplicatesCount ?? 0;
      });

      return total;
    }

    if (isLoading || !allDocCount) return 0;

    const countMapping: Record<
      string,
      keyof typeof allDocCount.likePopupCount
    > = {
      family_members: 'f',
      email_docs: 'e',
      near_duplicates: 'd',
    };

    const counts = allDocCount?.likePopupCount;
    if (!counts) return 0;

    const selected =
      actionToggles
        ?.filter((x) => x.selected)
        ?.map((x) => countMapping[x.id]) ?? [];

    if (selected.length === 0) {
      return allDocCount?.likePopupCount?.l ?? 0;
    } // No selected actions

    const allKeys = Object.keys(counts); // uses actual server order

    // Filter: must start with l
    const candidateKeys = allKeys.filter((k) => k.startsWith('l'));

    // Now find exact match:
    const matchKey = candidateKeys.find((k) => {
      const keyLetters = k.replace('l', '').split(''); // e.g. 'DF' → ['D','F']

      // Must contain all selected letters
      const containsAll = selected.every((s) => keyLetters.includes(s));

      // And must NOT contain any extra letters
      const noExtras = keyLetters.every((l) => selected.includes(l));

      return containsAll && noExtras;
    });

    if (matchKey !== undefined) return counts[matchKey] ?? 0;
    return 0;
  }, [
    isNonBg,
    nonBgCountData,
    nonBgDocCount,
    actionToggles,
    allDocCount,
    isLoading,
  ]);

  const titleInfoTooltip = (
    <ArrTooltipWrapper
      title={getT('tooltipTitle')}
      trigger={
        <IconButton
          aria-label="Info"
          className="arr-action-like__tooltip-button"
        >
          <InfoOutlinedIcon
            className="arr-action-like__tooltip-icon"
            fontSize="medium"
          />
        </IconButton>
      }
    >
      {getT('tooltipText1')}
      <div data-testid="tooltip-text">
        {getT('tooltipText1')}
        <br data-testid={`action-like-documents-br`} />
        <br data-testid={`action-like-documents-br-btn`} />
        {getT('tooltipText2')}
        <br data-testid={`action-like-documents-br-button`} />
        <br data-testid={`action-like-documents-br-icon`} />
        {getT('tooltipText3')}
      </div>
    </ArrTooltipWrapper>
  );

  const getAnalyticsIndicatorStatus = () => {
    if (analyticsIndicatorStatus == AnalyticsStatus.AnalyticsInProgress) {
      return getT('analyticsInProgress');
    } else if (
      analyticsIndicatorStatus == AnalyticsStatus.AnalyticsSkipped ||
      analyticsIndicatorStatus == AnalyticsStatus.AnalyticsFailed
    ) {
      return getT('analyticsInComplete');
    }
  };
  return (
    <ArrModal
      title={title}
      titleEndIcon={titleInfoTooltip}
      showModal={showModal}
      showClose={true}
      onClose={onClose}
      className="arr-action-like"
      description={ActionLikeDescription}
      dataTestId="arr-action-like-modal"
      buttons={[
        {
          id: '1',
          label: t('common.cancel'),
          onClick: onCancel,
          variant: 'secondary',
          dataTestId: 'alm-cancel-btn',
        },
        {
          id: '2',
          label: continueButtonLabel,
          onClick: () =>
            onContinue(
              isNonBg ? totalSelectedCount : backgroundCountData?.docCount
            ),
          variant: 'primary',
          dataTestId: 'btn-finalize-release-docs',
        },
      ]}
      {...props}
    >
      <ActionLike
        actionList={actionToggles}
        onActionChange={handleActionToggle}
        errorMessage={getAnalyticsIndicatorStatus()}
        isLoading={isLoading}
        totalSelectedCount={totalSelectedCount}
      />
    </ArrModal>
  );
};

export {ActionLikeDocuments};
export default ActionLikeDocuments;
